import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckIcon, TrashIcon, ArrowPathIcon, CameraIcon } from '@heroicons/react/24/outline';
import { Tip } from '../components/ui/tip';
import PlateScanner, { esPatenteValida, precalentarEscanerPatentes } from '../components/PlateScanner';
import OdometerScanner, { precalentarEscanerOdometro } from '../components/OdometerScanner';

/**
 * Etiquetado en tanda del dataset de visión (Svetovid). Acá caen los intentos
 * FALLIDOS de los escáneres: la chapa o el display que el modelo no pudo leer.
 * Escribir lo que dice la foto (o descartarla si no era nada) es lo que
 * convierte cada falla en una muestra para reentrenar.
 */

type Kind = 'patente' | 'odometro';
interface Pendiente { id: string; lectura: string; createdAt: string }
interface Stats { total: number; etiquetadas: number; sinEtiqueta: number; porFuente: Record<string, number> }

// La imagen requiere el token: se baja con axios y se muestra como blob
function ImagenMuestra({ kind, id, grande }: { kind: Kind; id: string; grande: boolean }) {
  const [src, setSrc] = useState('');
  useEffect(() => {
    let url = '';
    let vivo = true;
    axios.get(`/api/vision/sample/${kind}/${id}/image`, { responseType: 'blob' })
      .then((r) => { if (!vivo) return; url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => { /* sin imagen */ });
    return () => { vivo = false; if (url) URL.revokeObjectURL(url); };
  }, [kind, id]);
  if (!src) return <div className={`rounded-lg bg-muted ${grande ? 'h-48 w-full' : 'h-28 w-full max-w-[280px] sm:w-56'}`} />;
  return (
    <img
      src={src}
      alt="muestra"
      className={`rounded-lg border object-contain ${grande ? 'max-h-64 w-full' : 'w-full max-w-[280px] sm:w-56'}`}
    />
  );
}

export default function VisionDataset() {
  const [kind, setKind] = useState<Kind>('patente');
  const [pendientes, setPendientes] = useState<Pendiente[]>([]);
  const [stats, setStats] = useState<Record<string, Stats>>({});
  const [valores, setValores] = useState<Record<string, string>>({});
  const [cargando, setCargando] = useState(true);
  const inputsRef = useRef<Record<string, HTMLInputElement | null>>({});
  // Modo entrenamiento: escanear suelto (sin vehículo ni turno) solo para
  // alimentar el dataset. Cada lectura confirmada y cada intento fallido
  // los guarda el propio escáner; acá solo se lista lo de la sesión.
  const [escanerPatente, setEscanerPatente] = useState(false);
  const [escanerOdometro, setEscanerOdometro] = useState(false);
  const [sesion, setSesion] = useState<{ tipo: Kind; valor: string; hora: string }[]>([]);
  useEffect(() => {
    const t = setTimeout(() => { precalentarEscanerPatentes(); precalentarEscanerOdometro(); }, 500);
    return () => clearTimeout(t);
  }, []);
  const anotarLectura = (tipo: Kind, valor: string) => {
    setSesion((prev) => [{ tipo, valor, hora: new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }) }, ...prev].slice(0, 30));
  };

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [p, s] = await Promise.all([
        axios.get('/api/vision/pending', { params: { kind } }),
        axios.get('/api/vision/stats')
      ]);
      const lista: Pendiente[] = p.data?.pendientes || [];
      setPendientes(lista);
      setStats(s.data || {});
      // La lectura tentativa del escáner va precargada: casi siempre está a
      // uno o dos caracteres de la verdad
      setValores(Object.fromEntries(lista.map((m) => [m.id, m.lectura || ''])));
    } catch {
      toast.error('No se pudo cargar el dataset');
    } finally {
      setCargando(false);
    }
  }, [kind]);

  useEffect(() => { cargar(); }, [cargar]);

  const normalizar = (v: string) =>
    kind === 'patente'
      ? v.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7)
      : v.replace(/[^0-9]/g, '').slice(0, 7);

  const etiquetar = async (id: string) => {
    const label = normalizar(valores[id] || '');
    if (!label) return;
    if (kind === 'patente' && !esPatenteValida(label)) {
      toast.error('No tiene formato de patente argentina');
      return;
    }
    try {
      await axios.post(`/api/vision/sample/${kind}/${id}/label`, { label });
      setPendientes((prev) => prev.filter((m) => m.id !== id));
      setStats((prev) => {
        const s = prev[kind];
        return s ? { ...prev, [kind]: { ...s, etiquetadas: s.etiquetadas + 1, sinEtiqueta: Math.max(0, s.sinEtiqueta - 1) } } : prev;
      });
      // Foco al siguiente: etiquetar de corrido sin tocar el mouse
      const idx = pendientes.findIndex((m) => m.id === id);
      const sig = pendientes[idx + 1];
      if (sig) setTimeout(() => inputsRef.current[sig.id]?.focus(), 0);
    } catch {
      toast.error('No se pudo guardar la etiqueta');
    }
  };

  const descartar = async (id: string) => {
    try {
      await axios.delete(`/api/vision/sample/${kind}/${id}`);
      setPendientes((prev) => prev.filter((m) => m.id !== id));
      setStats((prev) => {
        const s = prev[kind];
        return s ? { ...prev, [kind]: { ...s, total: Math.max(0, s.total - 1), sinEtiqueta: Math.max(0, s.sinEtiqueta - 1) } } : prev;
      });
    } catch {
      toast.error('No se pudo descartar');
    }
  };

  const st = stats[kind];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Dataset de visión</h1>
          <p className="text-sm text-muted-foreground">
            Lo que los escáneres no pudieron leer. Escribí lo que dice la foto o descartala: cada etiqueta mejora el próximo modelo.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(['patente', 'odometro'] as Kind[]).map((k) => (
            <Button key={k} variant={kind === k ? 'default' : 'outline'} size="sm" onClick={() => setKind(k)}>
              {k === 'patente' ? 'Patentes' : 'Odómetros'}
              {stats[k]?.sinEtiqueta ? <span className="ml-2 rounded-full bg-amber-500/20 px-1.5 text-[11px] font-bold text-amber-700 dark:text-amber-300">{stats[k].sinEtiqueta}</span> : null}
            </Button>
          ))}
          <Tip label="Actualizar">
            <Button variant="outline" size="sm" onClick={cargar}><ArrowPathIcon className="h-4 w-4" /></Button>
          </Tip>
        </div>
      </div>

      {/* Modo entrenamiento: escanear para alimentar el dataset */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Modo entrenamiento</CardTitle>
          <CardDescription>
            Escaneá patentes y odómetros sueltos, sin cargar nada en el sistema: cada lectura confirmada
            y cada intento fallido queda guardado como muestra para reentrenar.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setEscanerPatente(true)}>
              <CameraIcon className="mr-2 h-4 w-4" />
              Escanear patente
            </Button>
            <Button variant="outline" onClick={() => setEscanerOdometro(true)}>
              <CameraIcon className="mr-2 h-4 w-4" />
              Escanear odómetro
            </Button>
          </div>
          {sesion.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {sesion.map((s, i) => (
                <span key={i} className="rounded-full border px-2.5 py-1 font-mono text-xs">
                  {s.tipo === 'patente' ? s.valor : `${Number(s.valor).toLocaleString('es-AR')} km`}
                  <span className="ml-1.5 font-sans text-muted-foreground">{s.hora}</span>
                </span>
              ))}
            </div>
          )}
          <PlateScanner
            open={escanerPatente}
            onClose={() => { setEscanerPatente(false); setTimeout(cargar, 800); }}
            onPlate={(p) => anotarLectura('patente', p)}
          />
          <OdometerScanner
            open={escanerOdometro}
            onClose={() => { setEscanerOdometro(false); setTimeout(cargar, 800); }}
            onKm={(km) => anotarLectura('odometro', String(km))}
          />
        </CardContent>
      </Card>

      {st && (
        <div className="grid grid-cols-3 gap-3">
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Muestras totales</div><div className="text-2xl font-bold">{st.total}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Etiquetadas (listas para entrenar)</div><div className="text-2xl font-bold">{st.etiquetadas}</div></CardContent></Card>
          <Card><CardContent className="p-4"><div className="text-xs text-muted-foreground">Sin etiquetar</div><div className="text-2xl font-bold">{st.sinEtiqueta}</div></CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Pendientes de etiquetar</CardTitle>
          <CardDescription>
            {kind === 'patente'
              ? 'Recortes de chapas tal como los vio el OCR. Enter guarda y pasa al siguiente.'
              : 'Fotos del tablero. Escribí el kilometraje que se lee en el display.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {cargando ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : pendientes.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nada pendiente. Las próximas fallas del escáner van a aparecer acá.</p>
          ) : (
            <div className="space-y-3">
              {pendientes.map((m) => (
                <div key={m.id} className="flex flex-col gap-3 rounded-xl border p-3 sm:flex-row sm:items-center">
                  <ImagenMuestra kind={kind} id={m.id} grande={kind === 'odometro'} />
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                    <div className="w-full text-[11px] text-muted-foreground">
                      {m.lectura ? <>El escáner leyó <span className="font-mono font-semibold">{m.lectura}</span> · </> : null}
                      {new Date(m.createdAt).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                    </div>
                    <Input
                      ref={(el) => { inputsRef.current[m.id] = el; }}
                      value={valores[m.id] || ''}
                      onChange={(e) => setValores((v) => ({ ...v, [m.id]: normalizar(e.target.value) }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); etiquetar(m.id); } }}
                      placeholder={kind === 'patente' ? 'AB123CD' : '123456'}
                      className="h-10 w-full font-mono text-lg font-bold tracking-widest sm:w-44"
                    />
                    <Tip label="Guardar etiqueta">
                      <Button size="sm" onClick={() => etiquetar(m.id)} disabled={!(valores[m.id] || '').length}>
                        <CheckIcon className="h-4 w-4" />
                      </Button>
                    </Tip>
                    <Tip label="Descartar (no era una patente / no se lee)">
                      <Button size="sm" variant="outline" className="text-destructive" onClick={() => descartar(m.id)}>
                        <TrashIcon className="h-4 w-4" />
                      </Button>
                    </Tip>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
